/* ============ basic strategy ============
   Pure functions of (total, dealer up, rules). Up-card values are 2–11 (A = 11).
   Action codes: H hit · S stand · D double · Ds double-else-stand ·
   P split · Rh surrender-else-hit · Rs surrender-else-stand ·
   Rp surrender-else-split

   DECK-AWARE (engine v5): 1- and 2-deck games have their own exception cells
   (effect of card removal is large at low deck counts). Every deck-conditional
   branch below was transcribed from the blackjackinfo.com strategy engine
   (1/2/6 decks × S17/H17 × DAS/noDAS, late surrender, peek) after verifying
   its 6-deck output matches this file's long-audited 4–8-deck baseline
   (see ENGINE-AUDIT.md). Baseline (4–8 decks) behavior is unchanged except
   8,8 vs A under H17+surrender (now Rp per WoO/BJInfo — CODE-REVIEW #9).

   All four table functions take the full settings object `st`
   ({ decks, h17, das, ... }); `dk` collapses decks to 1, 2, or 4 (= 4–8). */

import { hv, pairRank } from "./cards.js";

export const ACTION_NAME = { H: "Hit", S: "Stand", D: "Double", P: "Split", R: "Surrender" };

const dk = (st) => (st.decks <= 2 ? st.decks : 4);

export function hardAction(total, up, st) {
  const d = dk(st);
  if (total >= 17) return "S";
  if (total >= 13) return up <= 6 ? "S" : "H";
  if (total === 12) return up >= 4 && up <= 6 ? "S" : "H";
  // 11 vs A: multi-deck H17 only; 1–2 decks always (both S17/H17)
  if (total === 11) return up === 11 ? (st.h17 || d <= 2 ? "D" : "H") : "D";
  if (total === 10) return up <= 9 ? "D" : "H";
  // 9: 1–2 decks add the double vs 2
  if (total === 9) return up >= (d <= 2 ? 2 : 3) && up <= 6 ? "D" : "H";
  // 8: single deck doubles vs 5–6 (also reached by 4,4 without DAS)
  if (total === 8) return d === 1 && (up === 5 || up === 6) ? "D" : "H";
  return "H";
}

export function softAction(total, up, st) {
  const d = dk(st);
  if (total >= 20) return "S";
  // soft 19 vs 6: H17 all decks; single deck even S17
  if (total === 19) return up === 6 && (st.h17 || d === 1) ? "Ds" : "S";
  if (total === 18) {
    if (up <= 6) {
      if (d === 1 && up === 2) return "S"; // 1 deck: stand vs 2 (both S17/H17)
      return st.h17 || up >= 3 ? "Ds" : "S";
    }
    if (up <= 8) return "S";
    if (up === 11) return d === 1 && !st.h17 ? "S" : "H"; // 1 deck S17: stand vs A
    return "H"; // vs 9, 10
  }
  // soft 17: single deck adds the double vs 2
  if (total === 17) return up >= (d === 1 ? 2 : 3) && up <= 6 ? "D" : "H";
  if (total >= 15) return up >= 4 && up <= 6 ? "D" : "H";
  // soft 13–14: 1 deck doubles vs 4–6; 2-deck H17 soft 14 also vs 4–6
  if (total >= 13) {
    const lo = d === 1 || (d === 2 && total === 14 && st.h17) ? 4 : 5;
    return up >= lo && up <= 6 ? "D" : "H";
  }
  return "H";
}

export function surrAction(total, up, st, soft) {
  if (soft) return null;
  const d = dk(st);
  if (total === 17 && up === 11 && st.h17) return "Rs";
  // 16: multi surrenders vs 9+; 1–2 decks only vs 10/A
  if (total === 16) return up >= (d <= 2 ? 10 : 9) ? "Rh" : null;
  if (total === 15) {
    // vs 10: all deck counts except single deck
    if (up === 10 && d !== 1) return "Rh";
    if (up === 11 && st.h17) return "Rh";
  }
  return null;
}

export function pairAction(rank, up, st) {
  const d = dk(st);
  switch (rank) {
    case "A": return "P";
    case "10": return "S";
    case "9":
      if (up === 7 || up >= 10) {
        if (d === 1 && st.h17 && st.das && up === 11) return "P"; // 1 deck H17+DAS: split vs A
        return "S";
      }
      return "P";
    case "8":
      // H17 + surrender: surrender-else-split vs A — multi-deck, and 2 decks
      // without DAS. Never single deck (your two 8s soften the 16).
      if (up === 11 && st.h17 && (d >= 4 || (d === 2 && !st.das))) return "Rp";
      return "P";
    case "7":
      if (d === 1 && up === 10) return "Rs"; // 1 deck: two of the four 7s are gone
      if (d === 1 && up === 11 && st.h17) return "Rh";
      return up <= (d <= 2 && st.das ? 8 : 7) ? "P" : "H"; // 1–2 decks + DAS: split vs 8 too
    case "6": {
      const lo = d <= 2 || st.das ? 2 : 3;
      const hi = d <= 2 && st.das ? 7 : 6;
      return up >= lo && up <= hi ? "P" : "H";
    }
    case "5": return up <= 9 ? "D" : "H";
    case "4":
      // no DAS: falls through to hard 8 (single deck: D vs 5–6 emerges there)
      return st.das && up >= (d === 1 ? 4 : 5) && up <= 6 ? "P" : "H";
    case "3":
      if (st.das) return up <= (d === 1 ? 8 : 7) ? "P" : "H";
      return up >= 4 && up <= 7 ? "P" : "H";
    default: { // 2,2
      const lo = st.das ? 2 : d === 1 ? 3 : 4;
      return up >= lo && up <= 7 ? "P" : "H";
    }
  }
}

/* pure basic-strategy recommendation, respecting what's actually available */
export function recommend(hand, up, availability, st) {
  const { canSplit, canDouble, canSurrender } = availability;
  const cards = hand.cards;
  const { total, soft } = hv(cards);
  if (canSplit && cards.length === 2 && pairRank(cards[0]) === pairRank(cards[1])) {
    const pa = pairAction(pairRank(cards[0]), up, st);
    if (pa === "P") return "P";
    if (pa === "Rp") return canSurrender ? "R" : "P";
    if (pa === "Rs") return canSurrender ? "R" : "S";
    if (pa === "Rh") return canSurrender ? "R" : "H";
  }
  if (canSurrender) {
    const r = surrAction(total, up, st, soft);
    if (r === "Rh" || r === "Rs") return "R";
  }
  let a = soft ? softAction(total, up, st) : hardAction(total, up, st);
  if (a === "D") return canDouble ? "D" : "H";
  if (a === "Ds") return canDouble ? "D" : "S";
  return a;
}
